/**
 * Loads ALL bank transactions for a job by walking the paginated
 * `listAllBankTransactionsPaginated` query, computing dedup state
 * (fingerprint, isDuplicate, duplicateOfId) client-side over everything
 * loaded so far. Sorted by date ascending.
 *
 * Pagination is required server-side: large jobs exceed Convex's 16 MiB
 * per-execution read limit if every entry is read in a single query.
 *
 * Loading is progressive: a small first page paints the UI immediately,
 * then larger pages stream in the background (`isLoadingAll` stays true
 * until the last page). Rendering partial dedup results is safe because
 * pages arrive in `_creationTime` ascending order (the `by_job_id` index
 * order) and the dedup primary is the earliest-created row in a fingerprint
 * group — so later pages can only add NEW duplicates; rows already on
 * screen never flip from unique to duplicate.
 */

import { useEffect, useMemo } from "react";
import { usePaginatedQuery } from "convex/react";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  computeDedup,
  type DedupEntryInput,
} from "@cvx/jobs/summarization/agents/bankTransactions/dedupCore";

/** Small first page so the table paints fast on large jobs. */
const INITIAL_PAGE_SIZE = 200;
/**
 * Background page size. Entries average ~1 KB but can carry fat
 * changeHistory — 2000/page stays comfortably under the 16 MiB
 * per-execution read limit even at several KB per entry.
 */
const PAGE_SIZE = 2000;

type RawTxn = DedupEntryInput & { date: string } & Record<string, unknown>;

export function useAllBankTransactions(jobId: Id<"jobs">) {
  const { results, status, loadMore } = usePaginatedQuery(
    api.jobs.summarization.publicQueries.listAllBankTransactionsPaginated,
    { jobId },
    { initialNumItems: INITIAL_PAGE_SIZE },
  );

  useEffect(() => {
    if (status === "CanLoadMore") loadMore(PAGE_SIZE);
  }, [status, loadMore]);

  const transactions = useMemo(() => {
    if (status === "LoadingFirstPage") return undefined;
    const entries = results as RawTxn[];
    const dedup = computeDedup(entries);
    return entries
      .map((e) => {
        const d = dedup.get(e._id);
        return {
          ...e,
          fingerprint: d?.fingerprint,
          isDuplicate: d?.isDuplicate ?? false,
          duplicateOfId: d?.duplicateOfId,
        };
      })
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [results, status]);

  return {
    // Consumers cast to their own row shapes (BankTxnEntry / ExportTxn),
    // same as they did with the old v.any() query result.
    transactions: transactions as unknown[] | undefined,
    /** True only before the first page arrives. */
    isLoading: status === "LoadingFirstPage",
    /** True while background pages are still streaming in. */
    isLoadingAll: status !== "Exhausted",
    loadedCount: results.length,
  };
}
